/**
 * Deposit Receipt Page
 *
 * Displays and prints a deposit receipt.
 * Optimized for thermal printers (80mm width).
 */

import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'

import api from '@/lib/api'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/utils'

interface DepositReceiptData {
  business_name: string
  business_email: string
  business_id: string
  business_phone: string
  business_location: string
  deposit_id: number
  bill_id: number
  bill_number: string | null
  client_name: string
  bill_total: number
  bill_created_at: string
  deposit_amount: number
  total_deposited: number
  total_debts: number
  payment_type: string
  deposit_created_at: string
  cashier_name: string
}

export function DepositReceiptPage() {
  const { billId, depositId } = useParams<{ billId: string; depositId: string }>()
  const navigate = useNavigate()
  const printRef = useRef<HTMLDivElement>(null)
  const [receipt, setReceipt] = useState<DepositReceiptData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchReceipt = async () => {
      try {
        const response = await api.get(`/bills/${billId}/deposits/${depositId}/receipt`)
        setReceipt(response.data)
      } catch (err) {
        setError(t('receipts.errorLoading'))
        console.error(err)
      } finally {
        setLoading(false)
      }
    }

    if (billId && depositId) {
      fetchReceipt()
    }
  }, [billId, depositId])

  const handlePrint = () => {
    window.print()
  }

  const handleBack = () => {
    navigate(-1)
  }

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error || !receipt) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || t('receipts.notFound')}</p>
          <Button onClick={handleBack}>{t('common.back')}</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Print Controls - Hidden when printing */}
      <div className="no-print flex gap-2 mb-4 max-w-md mx-auto">
        <Button variant="outline" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Button>
        <Button onClick={handlePrint}>
          <Printer className="h-4 w-4 mr-2" />
          {t('receipts.print')}
        </Button>
      </div>

      {/* Receipt Content */}
      <div
        ref={printRef}
        className="receipt-container bg-white mx-auto p-4 shadow-lg"
        style={{ maxWidth: '80mm', fontFamily: 'Arial, sans-serif' }}
      >
        {/* Header */}
        <div className="text-center mb-4">
          <h1 className="text-lg font-bold">{receipt.business_name}</h1>
          <p className="text-xs">{receipt.business_email}</p>
          <p className="text-xs">{t('receipts.printed.idPrefix')} {receipt.business_id}</p>
          <p className="text-xs">{t('receipts.printed.phonePrefix')} {receipt.business_phone}</p>
          <p className="text-xs">{receipt.business_location}</p>
        </div>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-400 my-2"></div>

        {/* Receipt Type */}
        <div className="text-center mb-2">
          <h2 className="text-sm font-bold">{t('receipts.printed.depositReceipt')}</h2>
        </div>

        {/* Deposit Info */}
        <table className="w-full text-xs mb-2">
          <tbody>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.billHash')}</th>
              <td className="text-right">{receipt.bill_number || receipt.bill_id}</td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.paymentType')}</th>
              <td className="text-right">{receipt.payment_type}</td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.client')}</th>
              <td className="text-right">{receipt.client_name}</td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.billTotal')}</th>
              <td className="text-right">{formatCurrency(receipt.bill_total)}</td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.billDate')}</th>
              <td className="text-right">{receipt.bill_created_at}</td>
            </tr>
          </tbody>
        </table>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-400 my-2"></div>

        {/* Payment Details */}
        <table className="w-full text-xs">
          <tbody>
            <tr className="font-bold text-base">
              <th className="text-left">{t('receipts.printed.depositAmount')}</th>
              <td className="text-right">{formatCurrency(receipt.deposit_amount)}</td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.totalDeposited')}</th>
              <td className="text-right">{formatCurrency(receipt.total_deposited)}</td>
            </tr>
            <tr className="font-bold">
              <th className="text-left">{t('receipts.printed.pendingBalance')}</th>
              <td className="text-right">{formatCurrency(receipt.total_debts)}</td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.depositDate')}</th>
              <td className="text-right">{receipt.deposit_created_at}</td>
            </tr>
            <tr>
              <th className="text-left font-semibold">{t('receipts.printed.cashier')}</th>
              <td className="text-right">{receipt.cashier_name}</td>
            </tr>
          </tbody>
        </table>

        {/* Divider */}
        <div className="border-t border-dashed border-gray-400 my-2"></div>

        {/* Footer */}
        <div className="text-center text-xs mt-4">
          <p className="font-bold">{t('receipts.printed.thanksDeposit')}</p>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }
          body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          .receipt-container {
            box-shadow: none !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 2mm !important;
          }
          @page {
            size: 80mm auto;
            margin: 0;
          }
        }
      `}</style>
    </div>
  )
}
